import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { StripeElementLocale, StripeElementsOptions } from "@stripe/stripe-js"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { GatedConfirmSetupIntentWalletFailReason } from "ipctg-inflow-types/InFlowEnums"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import TextField from "@/globalComponents/molecules/TextField"
import {
  ExpectedError,
  GatedConfirmSetupIntentWalletError,
} from "@/utilities/expectedErrors"
import { STRIPE_PROMISE } from "@/utilities/globals"
import {
  meQueryOptions,
  savedPaymentMethodsOptions,
  walletSetupIntentOptions,
} from "@/utilities/hooks/queries/user"
import { Crumb } from "@/utilities/types/local"

export const Route = createFileRoute(
  "/_authenticated/account/saved-payment-methods/add"
)({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    context.queryClient.fetchQuery(meQueryOptions())
  },
  loader: () => {
    return {
      crumb: { translationKey: "account.savedPaymentMethods.header" } as Crumb,
    }
  },
})

function RouteComponent() {
  const { t, i18n } = useTranslation()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { data: setupIntentWalletRsp } = useSuspenseQuery(
    walletSetupIntentOptions()
  )

  const options: StripeElementsOptions = {
    clientSecret: setupIntentWalletRsp.clientSecret,
    locale: i18n.resolvedLanguage as StripeElementLocale,
    customerSessionClientSecret:
      setupIntentWalletRsp.customerSessionClientSecret || undefined,
    appearance: {
      variables: {
        fontFamily: "Inter",
      },
    },
    fonts: [
      {
        cssSrc:
          "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
      },
    ],
  }

  return (
    <Card>
      <Heading level={2}>{t("account.savedPaymentMethods.add.header")}</Heading>
      <Elements stripe={STRIPE_PROMISE} options={options}>
        <CardForm
          // We don't handle the redirect since we only do card here. No redirect required
          clientSecret={setupIntentWalletRsp.clientSecret}
          goNext={() =>
            navigate({
              to: "/account/saved-payment-methods",
              search,
            })
          }
        />
      </Elements>
    </Card>
  )
}

interface CardFormProps {
  goNext: () => void
  clientSecret: string
}

function CardForm(props: CardFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const [name, setName] = useState(
    me ? `${me.cognitoUser.given_name} ${me.cognitoUser.family_name}` : ""
  )
  const [nameError, setNameError] = useState<boolean>(false)
  const [error, setError] = useState<Error>()
  const { t } = useTranslation()

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <TextField
          value={name}
          onChange={(value) => {
            setNameError(!value)
            setName(value)
          }}
          isInvalid={!!nameError}
        >
          <Label>{t("account.savedPaymentMethods.add.nameLabel")}</Label>
          <TextField.Input />
          {nameError && <FieldError>{t("errors.common.required")}</FieldError>}
        </TextField>
        <TextField>
          <Label id="stripe-credit-card-element">
            {t("account.savedPaymentMethods.add.creditCardNumberLabel")}
          </Label>
          <div
            aria-labelledby="stripe-credit-card-element"
            className="input input--1 field--input"
          >
            <CardNumberElement
              options={{
                disableLink: true,
                showIcon: true,
                style: {
                  base: {
                    fontFamily: "Inter",
                    fontSize: "16px",
                  },
                },
              }}
              onChange={() => setError(undefined)}
              allow-top-navigation
            />
          </div>
        </TextField>
        <div className="form-row">
          <TextField>
            <Label id="stripe-credit-card-cvc-element">
              {t("account.savedPaymentMethods.add.creditCardCVCLabel")}
            </Label>
            <div
              aria-labelledby="stripe-credit-card-cvc-element"
              className="input input--1 field--input"
            >
              <CardCvcElement
                options={{
                  style: {
                    base: {
                      fontFamily: "Inter",
                      fontSize: "16px",
                    },
                  },
                }}
                onChange={() => setError(undefined)}
                allow-top-navigation
              />
            </div>
          </TextField>
          <TextField>
            <Label id="stripe-credit-card-expiry-element">
              {t("account.savedPaymentMethods.add.creditCardExpiryLabel")}
            </Label>
            <div
              aria-labelledby="stripe-credit-card-expiry-element"
              className="input input--1 field--input"
            >
              <CardExpiryElement
                options={{
                  style: {
                    base: {
                      fontFamily: "Inter",
                      fontSize: "16px",
                    },
                  },
                }}
                onChange={() => setError(undefined)}
                allow-top-navigation
              />
            </div>
          </TextField>
        </div>

        {error && (
          <FieldError className="center">
            {error instanceof ExpectedError
              ? t(error.translationKey)
              : error.message}
          </FieldError>
        )}
        <Button
          type="submit"
          hue="secondary"
          variant="default"
          isPending={loading}
        >
          {t("common.save")}
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    if (!stripe || !elements) {
      return
    }

    if (!name) {
      setNameError(true)
      return
    }

    const cardElement = elements.getElement(CardNumberElement)
    if (!cardElement) {
      setError(new Error("Card details not found. Please refresh the page."))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmCardSetup(props.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name,
          },
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      } else {
        const { cognitoIDToken } = await api.auth.fetchAuthSession()
        if (!cognitoIDToken) {
          throw new GatedConfirmSetupIntentWalletError(
            GatedConfirmSetupIntentWalletFailReason.Unauthorized
          )
        }
        const rsp = await api.confirmSetupIntentWallet.POST({
          cognitoIDToken,
          setupIntentID: result.setupIntent.id,
        })
        if ("failReason" in rsp) {
          throw new GatedConfirmSetupIntentWalletError(rsp.failReason)
        }

        queryClient.setQueryData(savedPaymentMethodsOptions().queryKey, {
          paymentMethods: rsp.paymentMethods,
        })
        props.goNext()
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e)
      } else if (e instanceof ExpectedError) {
        setError(new Error(t(e.translationKey)))
      } else {
        setError(new Error(t("errors.common.somethingWentWrong")))
      }
    } finally {
      setLoading(false)
    }
  }
}
